// AuthContext.js - WITH i18n TOAST TRANSLATIONS
// ✅ All toast messages now use translations

import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { authAPI, pdpAPI } from '../services/api';
import { supabase } from '../services/supabaseClient';
import api from '../services/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';  // ✅ ADD THIS

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();  // ✅ ADD THIS
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);
  const [creditsAvailable, setCreditsAvailable] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [hasLifetimeAccess, setHasLifetimeAccess] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const navigate = useNavigate();
  
  const setupInProgress = useRef(false);
  const claimAttempted = useRef(false);

  // Generate fingerprint
  const generateFingerprint = () => {
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const fingerprint = `${userAgent}|${language}|${screenWidth}x${screenHeight}|${timeZone}`;
    return btoa(fingerprint);
  };

  // Initialize fingerprint
  useEffect(() => {
    const fp = localStorage.getItem('fingerprint') || generateFingerprint();
    setFingerprint(fp);
    localStorage.setItem('fingerprint', fp);
    console.log('🔐 Fingerprint initialized:', fp.substring(0, 20) + '...');
    
    const loggingOut = sessionStorage.getItem('is_logging_out');
    if (loggingOut === 'true') {
      console.log('🧹 Clearing stale logout flag');
      sessionStorage.removeItem('is_logging_out');
    }
    
    let pendingAnalysis = localStorage.getItem('pendingAnalysis');
    if (!pendingAnalysis || pendingAnalysis === 'undefined' || pendingAnalysis === 'null') {
      pendingAnalysis = sessionStorage.getItem('pendingAnalysis');
      if (pendingAnalysis && pendingAnalysis !== 'undefined' && pendingAnalysis !== 'null') {
        localStorage.setItem('pendingAnalysis', pendingAnalysis);
      }
    }
    
    if (pendingAnalysis && pendingAnalysis !== 'undefined' && pendingAnalysis !== 'null') {
      console.log('📋 Found pending analysis on load:', pendingAnalysis);
      sessionStorage.setItem('pendingAnalysis', pendingAnalysis);
    }
  }, []);

  // ✅ Toast helper with i18n support
  const showToast = (type, message, toastId) => {
    try {
      const toastOptions = {
        toastId: toastId || `toast-${Date.now()}`,
        autoClose: 4000,
        position: "top-right",
      };

      switch(type) {
        case 'success':
          toast.success(message, toastOptions);
          break;
        case 'error':
          toast.error(message, toastOptions);
          break;
        case 'warning':
          toast.warning(message, toastOptions);
          break;
        case 'info':
        default:
          toast.info(message, toastOptions);
          break;
      }
    } catch (err) {
      console.error('Toast error:', err);
    }
  };

  // Fetch credits
  const fetchCredits = async () => {
    if (!user) {
      console.log('ℹ️ [FETCH_CREDITS] No user, skipping');
      return;
    }

    try {
      console.log('📞 [FETCH_CREDITS] Fetching for:', user.email);
      
      const response = await api.get('/api/analysis/credits/summary', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        timeout: 10000
      });

      console.log('🔥 [FETCH_CREDITS] Response:', response.data);

      if (response.data?.success) {
        const { current_balance, credits_used, lifetime_access } = response.data.data;
        const hasLifetime = Boolean(lifetime_access === true || lifetime_access === 1 || lifetime_access === '1');

        setCreditsAvailable(current_balance ?? 0);
        setCreditsUsed(credits_used ?? 0);
        setHasLifetimeAccess(hasLifetime);
        
        console.log('✅ [FETCH_CREDITS] Credits fetched:', {
          available: current_balance,
          used: credits_used,
          lifetime: hasLifetime
        });
      }
    } catch (err) {
      console.error('❌ [FETCH_CREDITS] Error:', err);
      
      if (err.response?.status === 401) {
        console.log('🔄 [FETCH_CREDITS] Token expired, refreshing...');
        const refreshed = await handleTokenRefresh();
        if (refreshed) {
          return fetchCredits();
        }
      }
    }
  };

  // ✅ Handle pending analysis claim with TRANSLATED toasts
  const handlePendingAnalysisClaim = async (userData, fp) => {
    if (claimAttempted.current) {
      console.log('⏭️ [CLAIM] Already attempted, skipping');
      return;
    }
    
    let pendingAnalysis = localStorage.getItem('pendingAnalysis');
    
    if (!pendingAnalysis || pendingAnalysis === 'undefined' || pendingAnalysis === 'null') {
      pendingAnalysis = sessionStorage.getItem('pendingAnalysis');
      if (pendingAnalysis && pendingAnalysis !== 'undefined' && pendingAnalysis !== 'null') {
        localStorage.setItem('pendingAnalysis', pendingAnalysis);
      }
    }
    
    console.log('🔍 [CLAIM] Checking pending analysis:', {
      pendingAnalysis,
      userEmail: userData?.email,
      fingerprint: fp?.substring(0, 20) + '...'
    });

    if (!pendingAnalysis || pendingAnalysis === 'undefined' || pendingAnalysis === 'null') {
      console.log('ℹ️ [CLAIM] No pending analysis to claim');
      return;
    }
    
    claimAttempted.current = true;
    
    try {
      console.log('📞 [CLAIM] Calling claimAnalysis API:', pendingAnalysis);
      const response = await pdpAPI.claimAnalysis(pendingAnalysis, fp);
      
      console.log('🔥 [CLAIM] Response:', response);

      if (!response.success) {
        // ✅ TRANSLATED: Already used warning
        if (response.code === 'FREE_ANALYSIS_ALREADY_USED') {
          localStorage.removeItem('pendingAnalysis');
          sessionStorage.removeItem('pendingAnalysis');
          
          console.log('⚠️ [CLAIM] User already used free analysis');
          showToast('warning', t('auth_toasts.already_used'), 'already-used');
          
          return;
        }
        
        // ✅ TRANSLATED: No credits warning
        if (response.code === 'NO_CREDITS') {
          localStorage.removeItem('pendingAnalysis');
          sessionStorage.removeItem('pendingAnalysis');
          showToast('warning', t('auth_toasts.no_credits'), 'no-credits');
          return;
        }

        throw new Error(response.error || 'Failed to claim analysis');
      }

      // ✅ TRANSLATED: Success message
      localStorage.removeItem('pendingAnalysis');
      sessionStorage.removeItem('pendingAnalysis');
      console.log('✅ [CLAIM] Analysis claimed successfully!');
      
      showToast('success', t('auth_toasts.analysis_claimed'), 'analysis-claimed');

      await fetchCredits();
      
      setTimeout(() => {
        navigate(`/analysis/${pendingAnalysis}`, { replace: true });
      }, 1500);

    } catch (err) {
      console.error('❌ [CLAIM] Error:', err);
      if (!err.message.includes('FREE_ANALYSIS_ALREADY_USED')) {
        // ✅ TRANSLATED: Error message
        showToast('error', t('auth_toasts.claim_error'), 'claim-error');
      }
    }
  };

  // Setup user session
  const setupUserSession = async (session, isLoginRedirect = false) => {
    if (setupInProgress.current) {
      console.log('⏭️ [SETUP] Already in progress, skipping');
      return;
    }
    
    setupInProgress.current = true;
    
    console.log('🔧 [SETUP] Setting up user session:', {
      userId: session.user.id,
      isLoginRedirect
    });

    try {
      localStorage.setItem('token', session.access_token);
      localStorage.setItem('refresh_token', session.refresh_token);

      await new Promise(resolve => setTimeout(resolve, 100));

      let userData;
      try {
        const response = await authAPI.getCurrentUser();
        
        if (response?.data?.user) {
          userData = response.data.user;
        }
      } catch (apiError) {
        console.log('⚠️ [SETUP] API user fetch failed, using session data');
      }

      if (!userData) {
        userData = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || 
                session.user.user_metadata?.full_name || 
                'User',
          created_at: session.user.created_at,
          updated_at: session.user.updated_at
        };
      }
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      console.log('✅ [SETUP] User data set:', userData.email);
      
      console.log('🔄 [SETUP] Fetching credits...');
      await fetchCredits();
      
      if (fingerprint) {
        const pending = localStorage.getItem('pendingAnalysis');
        if (pending && pending !== 'undefined' && pending !== 'null') {
          console.log('🎯 [SETUP] Will attempt to claim analysis in 1s');
          setTimeout(async () => {
            await handlePendingAnalysisClaim(userData, fingerprint);
          }, 1000);
        } else {
          console.log('ℹ️ [SETUP] No pending analysis → navigating to dashboard');
          
          // Only navigate if not already on dashboard or an auth-related page
          // NOTE: '/' (homepage) intentionally excluded — logged-in users can stay on homepage
          const currentPath = window.location.pathname;
          const shouldRedirect = ['/login', '/signup'].includes(currentPath) || 
                                  currentPath.startsWith('/auth');
          
          if (shouldRedirect) {
            console.log('✅ [SETUP] Redirecting to dashboard from:', currentPath);
            navigate('/dashboard');
          } else {
            console.log('✅ [SETUP] Already on correct page:', currentPath);
          }
        }
      }
      
      return userData;
      
    } finally {
      setTimeout(() => {
        setupInProgress.current = false;
      }, 2000);
    }
  };

  // Check if this is a login redirect
  const isLoginRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasGoogleParams = urlParams.has('access_token') || 
                            urlParams.has('refresh_token') ||
                            document.referrer.includes('accounts.google.com');
    
    const fromLogin = document.referrer.includes('/login') || 
                      sessionStorage.getItem('just_logged_in') === 'true';
    
    return hasGoogleParams || fromLogin;
  };

  // Auth state listener
  useEffect(() => {
    let eventCount = 0;
    const maxEvents = 3;
    
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 [AUTH] Event:', event);
      
      const isOriginalTab = !document.referrer.includes('email');
      
      const isLoggingOut = sessionStorage.getItem('is_logging_out') === 'true';
      const logoutTimestamp = sessionStorage.getItem('logout_timestamp');
      
      if (isLoggingOut && logoutTimestamp) {
        const timeSinceLogout = Date.now() - parseInt(logoutTimestamp);
        if (timeSinceLogout > 5000) {
          console.log('🧹 [AUTH] Logout flag is stale (>5s), clearing it');
          sessionStorage.removeItem('is_logging_out');
          sessionStorage.removeItem('logout_timestamp');
        } else if (event === 'SIGNED_IN') {
          console.log('⏭️ [AUTH] User is logging out, ignoring SIGNED_IN event');
          return;
        }
      } else if (isLoggingOut && event === 'SIGNED_IN') {
        console.log('🧹 [AUTH] Logout flag has no timestamp, clearing stale flag');
        sessionStorage.removeItem('is_logging_out');
      }
      
      eventCount++;
      if (eventCount > maxEvents) {
        console.log('⏭️ [AUTH] Too many auth events, ignoring');
        return;
      }
      
      setTimeout(() => {
        eventCount = 0;
      }, 5000);

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        if (isOriginalTab) {
          const isRedirect = isLoginRedirect();
          await setupUserSession(session, isRedirect);
          // ✅ Set loading false AFTER user is fully set, not before
          setLoading(false);
          setAuthInitialized(true);
          return; // skip the generic setLoading below
        } else {
          console.log('🔧 [AUTH] Email confirmation detected, skipping setup');
        }
      }

      if (event === 'SIGNED_OUT') {
        console.log('👋 [AUTH] User signed out event received');
        
        sessionStorage.removeItem('is_logging_out');
        sessionStorage.removeItem('logout_timestamp');
        console.log('✅ [AUTH] Logout flags cleared');
        
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setCreditsAvailable(0);
        setCreditsUsed(0);
        setHasLifetimeAccess(false);
        
        setupInProgress.current = false;
        claimAttempted.current = false;
      }

      if (!authInitialized) {
        setAuthInitialized(true);
        setLoading(false);
      }
    });

    return () => {
      listener?.subscription.unsubscribe();
    };
  }, [fingerprint, t]);  // ✅ ADD 't' to dependencies

  // Initial session check
  useEffect(() => {
    if (!authInitialized) return;

    const checkInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) return;

        if (session && !user && !setupInProgress.current) {
          const isRedirect = isLoginRedirect();
          await setupUserSession(session, isRedirect);
        }
      } catch (err) {
        console.error('[AUTH] Session check error:', err);
      }
    };

    checkInitialSession();
  }, [authInitialized]);

  // Fetch credits when user changes
  useEffect(() => {
    if (user) {
      console.log('👤 [AUTH] User changed, fetching credits for:', user.email);
      fetchCredits();
    } else {
      console.log('👤 [AUTH] No user, resetting credits');
      setCreditsAvailable(0);
      setCreditsUsed(0);
      setHasLifetimeAccess(false);
    }
  }, [user]);

  // Token refresh
  const handleTokenRefresh = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error || !session) {
        await logout();
        return false;
      }
      
      localStorage.setItem('token', session.access_token);
      localStorage.setItem('refresh_token', session.refresh_token);
      
      return true;
    } catch (err) {
      await logout();
      return false;
    }
  };

  // Login
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
  
    try {
      console.log('🔐 [LOGIN] Attempting login for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
  
      if (error) {
        console.error('❌ [LOGIN] Supabase error:', error);
        
        // 🔥 NEW: Check for specific error types
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid credentials')) {
          return {
            success: false,
            error: 'auth.invalid_credentials',
            code: 'INVALID_CREDENTIALS'
          };
        } else if (errorMsg.includes('email not confirmed') || errorMsg.includes('verify your email')) {
          return {
            success: false,
            error: 'auth.email_not_confirmed',
            code: 'EMAIL_NOT_CONFIRMED'
          };
        } else if (errorMsg.includes('too many requests') || errorMsg.includes('rate limit')) {
          return {
            success: false,
            error: 'auth.rate_limit',
            code: 'RATE_LIMIT'
          };
        }
        
        throw error;
      }
      
      if (!data || !data.session) {
        return {
          success: false,
          error: 'auth.error_login',
          code: 'NO_SESSION'
        };
      }

      console.log('✅ [LOGIN] Login successful for:', email);

      sessionStorage.setItem('just_logged_in', 'true');
      
      // Navigate happens inside setupUserSession after user is fully set
      return { success: true };
      
    } catch (err) {
      console.error('❌ [LOGIN] Unexpected error:', err);
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        code: 'LOGIN_ERROR'
      };
    } finally {
      setLoading(false);
    }
  };

  // Register
  const register = async (name, email, password) => {
    setLoading(true);
    setError(null);
  
    try {
      console.log('📝 [REGISTER] Starting registration for:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name }
        }
      });
  
      if (error) {
        console.error('❌ [REGISTER] Supabase error:', error);
        
        // 🔥 CRITICAL FIX: Check for specific error types
        const errorMsg = error.message.toLowerCase();
        
        // 🎯 CASO SPECIALE: User registered with Google OAuth
        if (errorMsg.includes('user already registered') || errorMsg.includes('already registered')) {
          console.log('⚠️ [REGISTER] User already registered - checking provider...');
          
          // Try to determine if it's a Google user by attempting password login
          // If password login fails, it's likely a Google-only account
          return { 
            success: false, 
            error: 'auth.user_exists_check_provider',
            code: 'USER_EXISTS',
            suggestion: 'Try signing in with Google if you registered with Google'
          };
        } 
        else if (errorMsg.includes('already exists')) {
          return { 
            success: false, 
            error: 'auth.user_exists',
            code: 'USER_EXISTS'
          };
        } 
        else if (errorMsg.includes('invalid email') || errorMsg.includes('email address is invalid')) {
          return {
            success: false,
            error: 'auth.invalid_email',
            code: 'INVALID_EMAIL'
          };
        } 
        else if (errorMsg.includes('password') && (errorMsg.includes('short') || errorMsg.includes('at least') || errorMsg.includes('weak'))) {
          return {
            success: false,
            error: 'auth.weak_password',
            code: 'WEAK_PASSWORD'
          };
        } 
        else if (errorMsg.includes('rate limit')) {
          return {
            success: false,
            error: 'auth.rate_limit',
            code: 'RATE_LIMIT'
          };
        }
        
        throw error;
      }
  
      // 🔥 CRITICAL CHECK: If signup succeeded but user was already confirmed, 
      // it means Supabase auto-confirmed them (this happens when email already exists)
      const emailConfirmed = data?.user?.email_confirmed_at !== null;
      const hasSession = !!data?.session;
      
      console.log('✅ [REGISTER] Registration response:', {
        email,
        emailConfirmed,
        hasSession,
        userId: data?.user?.id
      });
  
  
      if (!hasSession) {
        // Email confirmation required - no session returned
        return { 
          success: true,
          emailConfirmed: false,
          needsEmailConfirmation: true,
          successMessage: 'auth.success_check_email'
        };
      }

      // Email already confirmed (auto-confirm enabled in Supabase)
      return { 
        success: true,
        emailConfirmed: true,
        successMessage: 'auth.success_email_confirmed'
      };
      
    } catch (err) {
      console.error('❌ [REGISTER] Unexpected error:', err);
      setError(err.message || 'Registration failed');
      return { 
        success: false, 
        error: err.message || 'auth.error_registration',
        code: 'REGISTRATION_ERROR'
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    console.log('🔴 [LOGOUT] Starting logout process');
    
    const pendingAnalysis = localStorage.getItem('pendingAnalysis');
    const fingerprint = localStorage.getItem('fingerprint');
    const cookieConsent = localStorage.getItem('betterpdp_consent');
    
    localStorage.clear();
    sessionStorage.clear();
    
    if (fingerprint) {
      localStorage.setItem('fingerprint', fingerprint);
    }
    if (pendingAnalysis && pendingAnalysis !== 'undefined' && pendingAnalysis !== 'null') {
      localStorage.setItem('pendingAnalysis', pendingAnalysis);
    }
    if (cookieConsent && cookieConsent !== 'undefined' && cookieConsent !== 'null') {
      localStorage.setItem('betterpdp_consent', cookieConsent);
    }
    
    console.log('✅ [LOGOUT] Storage cleared (preserved: fingerprint, pendingAnalysis, consent)');
    
    window.location.href = '/';
    
    setLoading(false);
    setUser(null);
    setCreditsAvailable(0);
    setCreditsUsed(0);
    setHasLifetimeAccess(false);
    setupInProgress.current = false;
    claimAttempted.current = false;
  };

  // Track free analysis
  const trackFreeAnalysis = (analysisId) => {
    if (analysisId && analysisId !== 'undefined' && analysisId !== 'null') {
      console.log('📖 [TRACK] Tracking pending analysis:', analysisId);
      localStorage.setItem('pendingAnalysis', analysisId);
      sessionStorage.setItem('pendingAnalysis', analysisId);
      
      const saved = localStorage.getItem('pendingAnalysis');
      console.log('✅ [TRACK] Verified pending analysis saved:', saved);
    }
  };
  
  // Check if free analysis used
  const hasFreeAnalysisUsed = () => {
    const pending = localStorage.getItem('pendingAnalysis');
    return pending !== null && pending !== 'undefined' && pending !== 'null';
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pending = localStorage.getItem('pendingAnalysis');
      const redirectUrl = pending && pending !== 'undefined' && pending !== 'null'
        ? `${window.location.origin}/dashboard`
        : `${window.location.origin}/dashboard`;
      
      console.log(`🔀 [GOOGLE] OAuth redirect will be: ${redirectUrl}`);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('❌ [GOOGLE] OAuth error:', error);
        setError(error.message);
        return { 
          success: false, 
          error: error.message || 'Failed to sign in with Google' 
        };
      }

      console.log('✅ [GOOGLE] OAuth initiated');
      return { success: true };
      
    } catch (err) {
      console.error('❌ [GOOGLE] Sign in error:', err);
      setError(err.message);
      return { 
        success: false, 
        error: err.message || 'Failed to sign in with Google' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Refresh credits
  const refreshCredits = async () => {
    console.log('🔄 [REFRESH_CREDITS] Manual refresh triggered');
    return await fetchCredits();
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    signInWithGoogle,
    isAuthenticated: !!user,
    trackFreeAnalysis,
    hasFreeAnalysisUsed,
    fingerprint,
    creditsAvailable,
    creditsUsed,
    hasCredits: creditsAvailable > 0,
    hasLifetimeAccess,
    fetchCredits,
    refreshCredits,
    handleTokenRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};